/**
 * Splits a string into matched and unmatched runs for highlighting.
 *
 * F6 requires the matched term highlighted in a result. The match on the
 * server is a full-text, accent-insensitive, prefix match (§5.4a), so a naive
 * client-side `indexOf` would not reproduce it exactly — a search for `terra`
 * highlighting nothing in "Terraváult" because of the accent would read as a
 * bug even though the record matched correctly.
 *
 * So the rule here is deliberately looser than the server's and errs towards
 * marking something: each whitespace-separated term in the query highlights any
 * run of letters it is a case-insensitive, accent-insensitive PREFIX of. That
 * catches the common cases the reader expects to see lit up without pretending
 * to be the tsquery.
 *
 * Returned as segments rather than HTML: the caller renders them through React,
 * so a title containing `<` cannot become markup (the injection path a
 * `dangerouslySetInnerHTML` highlighter opens).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.11, PRD F6, §5.4a
 */

/** One run of the source string, flagged as matched or not. */
export interface HighlightSegment {
  readonly text: string;
  readonly matched: boolean;
}

/** Strips accents so `terra` matches `Terravált`, matching the server. */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * The distinct query terms, folded and longest-first.
 *
 * Longest-first so that when two terms are prefixes of each other ("terra" and
 * "terraváult"), the longer one wins the match and lights up more of the word
 * rather than stopping short.
 */
function queryTerms(query: string): string[] {
  const terms = new Set(
    query
      .split(/\s+/)
      .map(fold)
      .filter((term) => term.length > 0),
  );
  return [...terms].sort((a, b) => b.length - a.length);
}

/**
 * Whether a word starts with any query term.
 *
 * Returns the length of the matching prefix, or 0. The folded comparison keeps
 * accents from breaking a match while the returned length indexes back into the
 * ORIGINAL word, so the accented characters are preserved in the output.
 */
function matchLength(word: string, terms: readonly string[]): number {
  const folded = fold(word);
  for (const term of terms) {
    if (folded.startsWith(term)) {
      return term.length;
    }
  }
  return 0;
}

/**
 * Breaks `source` into matched and unmatched segments.
 *
 * Adjacent segments of the same kind are never produced, so a caller can key
 * on the index without collapsing runs. An empty query, or one with no usable
 * terms, returns the whole string as a single unmatched segment — the reader
 * still sees the text, just unlit.
 */
export function highlightSegments(source: string, query: string): HighlightSegment[] {
  const terms = queryTerms(query);
  if (terms.length === 0 || source === '') {
    return source === '' ? [] : [{ text: source, matched: false }];
  }

  const segments: HighlightSegment[] = [];
  /* Split on word boundaries so a term prefix-matches a word rather than the
     middle of one — "id" should not light up inside "David". The separators
     are kept (the capturing group) so the reassembled string is intact. */
  const parts = source.split(/(\P{L}+)/u).filter((part) => part !== '');

  for (const part of parts) {
    const length = matchLength(part, terms);

    if (length === 0) {
      pushSegment(segments, part, false);
      continue;
    }

    pushSegment(segments, part.slice(0, length), true);
    if (length < part.length) {
      pushSegment(segments, part.slice(length), false);
    }
  }

  return segments;
}

/**
 * Appends a segment, merging it into the previous one when the kind matches.
 *
 * Merging keeps the output free of adjacent same-kind runs, so a caller can
 * key on the index. The last element is replaced via `splice` rather than
 * index assignment so the parameter array is not written through a subscript,
 * which the lint rules treat as reassigning a parameter's property.
 */
function pushSegment(segments: HighlightSegment[], text: string, matched: boolean): void {
  const last = segments.at(-1);
  if (last?.matched === matched) {
    segments.splice(segments.length - 1, 1, { text: last.text + text, matched });
    return;
  }
  segments.push({ text, matched });
}
